import { useState, useEffect, type MouseEventHandler } from 'react';
import { useStripe } from '@stripe/react-stripe-js';
import type { CanMakePaymentResult, PaymentRequest } from '@stripe/stripe-js';
import { handleApplePayMethod, handleGooglePayMethod } from '../../../../../utils/payments/useCardFormListeners';
import { type UserProfile } from '../../../../../utils/api/schema/profileSchema';
import GooglePayIcon from './GooglePayIcon';
import ApplePayIcon from './ApplePayIcon';
import styles from './PaymentRequestForm.scss';

interface PaymentRequestFormProps {
  profile: UserProfile;
  onGooglePayAvailable: () => void;
  onApplePayAvailable: () => void;
}

const PaymentRequestForm = ({ profile, onGooglePayAvailable, onApplePayAvailable }: PaymentRequestFormProps) => {
  const stripe = useStripe();

  const [paymentRequest, setPaymentRequest] = useState<PaymentRequest | null>(null);
  const [availableMethods, setAvailableMethods] = useState<CanMakePaymentResult | null>(null);

  useEffect(() => {
    if (!stripe?.paymentRequest) {
      return;
    }

    const request = stripe.paymentRequest({
      currency: profile.subscription?.price?.currency.toLowerCase() || '',
      country: 'CY',
      total: {
        amount: Number.parseInt(profile.subscription?.price?.amount || ''),
        label: profile.subscription?.name || '',
      },
      disableWallets: ['browserCard'],
    });

    request
      .canMakePayment()
      .then((result) => {
        if (!result) {
          return;
        }

        setPaymentRequest(request);
        setAvailableMethods(result);

        if (result.applePay) {
          onApplePayAvailable();
          request.on('paymentmethod', ({ paymentMethod, complete }) => {
            handleApplePayMethod(stripe, paymentMethod, complete);
          });
        } else if (result.googlePay) {
          onGooglePayAvailable();
          request.on('paymentmethod', ({ paymentMethod, complete }) => {
            handleGooglePayMethod(stripe, paymentMethod, complete);
          });
        }
      })
      .catch(() => {});
  }, []);

  const onClick: MouseEventHandler<HTMLButtonElement> = (e) => {
    e.preventDefault();
    paymentRequest?.show();
  };

  return (
    <div className={styles.wrapper}>
      {paymentRequest ? (
        <button type="button" className={styles.button} onClick={onClick}>
          {availableMethods?.applePay ? <ApplePayIcon isSelected /> : null}
          {availableMethods?.googlePay ? <GooglePayIcon whiteFont /> : null}
        </button>
      ) : null}
    </div>
  );
};

export default PaymentRequestForm;
